/**
 * 资源加载与缓存：图片（Image）与音频（fetch + decodeAudioData → AudioBuffer）。
 * AudioContext 由 audio.js 提供。
 */
import { AudioSys } from './audio.js';

export const Assets = {
  /** @type {Map<string, HTMLImageElement>} */
  images: new Map(),
  /** @type {Map<string, AudioBuffer>} */
  audios: new Map(),
  /** 加载进度 0..1 */
  progress: 0,
  /** 音频 key → 实际 URL（含指纹 query；main.js 装配时注册，懒加载用） */
  audioUrls: {},
  /** 懒加载 in-flight 去重表：key → Promise */
  _audioPromises: new Map(),

  /**
   * 懒加载一首音频（BGM 按需加载的核心）：
   * - 已加载 / 已在拉取 → 复用结果与 in-flight promise（调用方共享同一请求）；
   * - 永远 resolve 不 reject：加载失败保持"BGM 缺失静默"语义，不 block 游戏；
   * - 无 AudioContext（node 校验进程）直接 resolve。
   * @param {string} key manifest 音频 key
   * @returns {Promise<void>}
   */
  ensureAudio(key) {
    if (this.audios.has(key)) return Promise.resolve(true);
    if (this._audioPromises.has(key)) return this._audioPromises.get(key);
    const url = this.audioUrls[key];
    if (!url) return Promise.resolve(false); // 未注册（dev 直载全部已加载/无此 key）
    // set 先注册、delete 挂 then 后清（顺序反了会留僵尸 in-flight：音频环境瞬时不可用时
    // 该曲永不再尝试加载——审计发现）；返回 boolean 表示是否真正拿到 buffer。
    const p = (async () => {
      try {
        const ctx = AudioSys.ensureCtx();
        if (!ctx) return false;
        // 10s 超时兜底：弱网挂起不得让 block 点（进关/credits）永久等待
        const ctl = new AbortController();
        const timer = setTimeout(() => ctl.abort(), 10000);
        try {
          const res = await fetch(url, { signal: ctl.signal });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const buf = await ctx.decodeAudioData(await res.arrayBuffer());
          this.audios.set(key, buf);
          return true;
        } finally {
          clearTimeout(timer);
        }
      } catch (e) {
        console.warn(`[Assets] 懒加载音频失败: ${key}`, e);
        return false;
      }
    })();
    this._audioPromises.set(key, p);
    p.then(() => this._audioPromises.delete(key));
    return p;
  },

  /**
   * 并发加载全部资源。
   * @param {{images?: Object<string,string>, audios?: Object<string,string>}} manifest key → url
   * @param {(progress: number) => void} [onProgress] 0..1 回调
   * @returns {Promise<void>}
   */
  async load({ images = {}, audios = {} } = {}, onProgress) {
    const imageEntries = Object.entries(images);
    const audioEntries = Object.entries(audios);
    const total = imageEntries.length + audioEntries.length;
    let done = 0;
    this.progress = total === 0 ? 1 : 0;

    const tick = () => {
      done++;
      this.progress = total === 0 ? 1 : done / total;
      if (onProgress) onProgress(this.progress);
    };

    const imageJobs = imageEntries.map(([key, url]) =>
      new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          this.images.set(key, img);
          tick();
          resolve();
        };
        img.onerror = () => {
          console.warn(`[Assets] 图片加载失败: ${key} ${url}`);
          tick();
          resolve(); // 缺图不阻塞启动
        };
        img.src = url;
      })
    );

    const audioJobs = audioEntries.map(async ([key, url]) => {
      try {
        const ctx = AudioSys.ensureCtx();
        if (!ctx) return;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const buf = await ctx.decodeAudioData(await res.arrayBuffer());
        this.audios.set(key, buf);
      } catch (e) {
        console.warn(`[Assets] 音频加载失败: ${key} ${url}`, e);
      } finally {
        tick();
      }
    });

    await Promise.all([...imageJobs, ...audioJobs]);
  },

  /**
   * 取已加载图片。
   * @param {string} key
   * @returns {HTMLImageElement|undefined}
   */
  img(key) {
    return this.images.get(key);
  },

  /**
   * 取已解码音频。
   * @param {string} key
   * @returns {AudioBuffer|undefined}
   */
  audio(key) {
    return this.audios.get(key);
  },
};
